import { useEffect, useMemo, useState, useRef } from 'react';
import { X, Network, Server, Wifi, MapPin, Check, AlertTriangle, Circle, Tag, Plus, Loader2, ChevronDown } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { CustomDropdown } from '../../components/CustomDropdown ';
import { jwt_decode } from '../../utils/jwt_decode';

type Mode = 'add' | 'edit';

interface TagData {
    id: number;
    name: string;
    organization_id?: number;
    client_id?: number | null;
}

export interface Asset {
    id: number;
    name: string;
    asset_type: string;
    hostname: string;
    ip_address: string;
    location: string;
    switch_role: string | null;
    total_ports: number | null;
    is_primary: boolean;
    tags: string;
    resolved_tags?: string | any[];
    tag_ids?: number[];
    purchase_date?: string | null;
    installation_date?: string | null;
    warranty_expiration?: string | null;
    warranty_details?: string | null;
    amc_expiration?: string | null;
    amc_provider?: string | null;
    amc_details?: string | null;
    end_of_life?: string | null;
    end_of_support?: string | null;
}

interface AddAssetModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    mode?: Mode;
    asset?: Asset | null;
}

const emptyForm = {
    name: '',
    asset_type: 'switch',
    hostname: '',
    ip_address: '',
    location: '',
    switch_role: '',
    total_ports: '',
    is_primary: false,
    purchase_date: '',
    installation_date: '',
    warranty_expiration: '',
    warranty_details: '',
    amc_expiration: '',
    amc_provider: '',
    amc_details: '',
    end_of_life: '',
    end_of_support: ''
};

const isValidIPv4 = (ip: string): boolean => {
    const ipv4Regex =
        /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipv4Regex.test(ip);
};

const AddAssetModal: React.FC<AddAssetModalProps> = ({
    open,
    onClose,
    onSuccess,
    mode = 'add',
    asset = null
}) => {
    const [formData, setFormData] = useState(emptyForm);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Tag management states
    const [selectedTags, setSelectedTags] = useState<TagData[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [availableTags, setAvailableTags] = useState<TagData[]>([]);
    const [isTagLoading, setIsTagLoading] = useState(false);
    const [isCreatingTag, setIsCreatingTag] = useState(false);
    const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);
    const tagDropdownRef = useRef<HTMLDivElement>(null);

    const storedLevel = localStorage.getItem('level');
    const level = storedLevel ? JSON.parse(storedLevel) : null;
    const decodedToken = jwt_decode();
    const organizationId = decodedToken?.organization_id;

    // UPDATE 1: Added 'firewall' to showPortFields
    const showPortFields = useMemo(() => ['switch', 'router', 'firewall', 'unmanaged_switch'].includes(formData.asset_type), [formData.asset_type]);
    const showPrimaryOption = useMemo(() => formData.asset_type === 'switch' && formData.switch_role === 'core', [formData.asset_type, formData.switch_role]);

    // Close tag dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target as Node)) {
                setIsTagDropdownOpen(false);
            }
        };

        if (isTagDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isTagDropdownOpen]);

    const fetchTags = async (query: string = '') => {
        setIsTagLoading(true);
        try {
            const queryObj: any = {};
            if (level?.id) {
                queryObj.client_id = level.id;
            }
            if (query.trim()) {
                queryObj.name = { "$ilike": `%${query}%` };
            }

            const params = {
                query: JSON.stringify(queryObj),
                limit: 20
            };

            const response = await baseapi.get('/tags', { params });
            if (response.data.status) {
                setAvailableTags(response.data.data);
            }
        } catch (error: any) {
            console.error('Failed to fetch tags', error);
        } finally {
            setIsTagLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        const timer = setTimeout(() => {
            fetchTags(searchTerm);
        }, 200);
        return () => clearTimeout(timer);
    }, [searchTerm, open]);

    useEffect(() => {
        if (!open) return;

        if (mode === 'edit' && asset) {
            setFormData({
                name: asset.name ?? '',
                asset_type: asset.asset_type ?? 'switch',
                hostname: asset.hostname ?? '',
                ip_address: asset.ip_address ?? '',
                location: asset.location ?? '',
                switch_role: asset.switch_role ?? '',
                total_ports: asset.total_ports != null ? String(asset.total_ports) : '',
                is_primary: asset.is_primary ?? false,
                purchase_date: asset.purchase_date ?? '',
                installation_date: asset.installation_date ?? '',
                warranty_expiration: asset.warranty_expiration ?? '',
                warranty_details: asset.warranty_details ?? '',
                amc_expiration: asset.amc_expiration ?? '',
                amc_provider: asset.amc_provider ?? '',
                amc_details: asset.amc_details ?? '',
                end_of_life: asset.end_of_life ?? '',
                end_of_support: asset.end_of_support ?? ''
            });

            // Populate selected tags from resolved_tags if available
            if (asset.resolved_tags) {
                try {
                    const parsed = typeof asset.resolved_tags === 'string' ? JSON.parse(asset.resolved_tags) : asset.resolved_tags;
                    if (Array.isArray(parsed)) {
                        setSelectedTags(parsed);
                    } else {
                        setSelectedTags([]);
                    }
                } catch {
                    setSelectedTags([]);
                }
            } else {
                setSelectedTags([]);
            }
        } else {
            setFormData(emptyForm);
            setSelectedTags([]);
        }

        setSearchTerm('');
        setIsTagDropdownOpen(false);
        setErrors({});
        fetchTags('');
    }, [open, mode, asset]);

    const handleCreateTag = async () => {
        if (!searchTerm.trim()) return;
        setIsCreatingTag(true);
        try {
            const tagPayload: any = {
                name: searchTerm.trim(),
                ...(level?.id && { client_id: level.id })
            };
            const response = await baseapi.post('/tags', tagPayload);
            if (response.data.status) {
                const newTag: TagData = {
                    id: response.data.data.id,
                    name: searchTerm.trim(),
                    organization_id: 0,
                    client_id: level?.id || null
                };
                showSuccess('Tag created');
                setSelectedTags(prev => [...prev, newTag]);
                setSearchTerm('');
                fetchTags('');
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setIsCreatingTag(false);
        }
    };

    const handleToggleTag = (tag: TagData) => {
        setSelectedTags(prev => {
            const isSelected = prev.find(t => t.id === tag.id);
            if (isSelected) return prev.filter(t => t.id !== tag.id);
            return [...prev, tag];
        });
    };

    const assetTypes = [
        { label: 'Switch', value: 'switch' },
        { label: 'Router', value: 'router' },
        { label: 'Firewall', value: 'firewall' },
        { label: 'Server', value: 'server' },
        { label: 'Endpoint Server', value: 'local_asset' },
        { label: 'Camera', value: 'camera' },
        { label: 'Access Point', value: 'access_point' },
        { label: 'ILO', value: 'ilo' },
        { label: 'Printer', value: 'printer' },
        { label: 'Storage', value: 'storage' },
        { label: 'Unmanaged Switch', value: 'unmanaged_switch' },
        { label: 'NVR', value: 'nvr' },
        { label: 'Other', value: 'other' }
    ];

    const switchRoles = [
        { label: 'Core', value: 'core' },
        { label: 'Distribution', value: 'distribution' },
        { label: 'Access', value: 'access' },
        { label: 'Edge', value: 'edge' }
    ];

    const handleClose = () => {
        setFormData(emptyForm);
        setSelectedTags([]);
        setSearchTerm('');
        setIsTagDropdownOpen(false);
        setErrors({});
        onClose();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        if (errors[name]) {
            setErrors(prev => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }

        if (name === 'total_ports') {
            if (value === '') {
                setFormData(prev => ({ ...prev, total_ports: '' }));
                return;
            }
            const n = Number(value);
            if (!Number.isFinite(n) || n < 0) return;
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const validateForm = (): boolean => {
        const next: Record<string, string> = {};

        if (!formData.name.trim()) next.name = 'Name is required';

        const ip = formData.ip_address.trim();
        if (formData.asset_type !== 'unmanaged_switch') {
            if (!ip) next.ip_address = 'IP Address is required';
            else if (!isValidIPv4(ip)) next.ip_address = 'Invalid IPv4 address';
        }

        if (showPortFields && formData.total_ports) {
            const ports = Number(formData.total_ports);
            if (!Number.isInteger(ports) || ports < 1 || ports > 999) next.total_ports = 'Ports must be 1 to 999';
        }

        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) {
            showError('Please fix validation errors');
            return;
        }

        try {
            setLoading(true);

            const payload: any = {
                client_id: level?.id,
                organization_id: Number(organizationId),
                name: formData.name.trim(),
                asset_type: formData.asset_type,
                hostname: formData.hostname?.trim() || null,
                ip_address: formData.ip_address.trim(),
                location: formData.location?.trim() || null,
                tag_ids: selectedTags.map(t => t.id),
                status: 'active'
            };

            // UPDATE 2: Include 'firewall' in payload logic
            if (['switch', 'router', 'firewall', 'unmanaged_switch'].includes(formData.asset_type)) {
                payload.switch_role = formData.switch_role || null;
                payload.total_ports = formData.total_ports ? Number(formData.total_ports) : null;

                if (formData.switch_role === 'core') {
                    payload.is_primary = formData.is_primary;
                } else {
                    payload.is_primary = false;
                }
            }

            // Lifecycle & Warranty Fields
            payload.purchase_date = formData.purchase_date || null;
            payload.installation_date = formData.installation_date || null;
            payload.warranty_expiration = formData.warranty_expiration || null;
            payload.warranty_details = formData.warranty_details?.trim() || null;
            payload.amc_expiration = formData.amc_expiration || null;
            payload.amc_provider = formData.amc_provider?.trim() || null;
            payload.amc_details = formData.amc_details?.trim() || null;
            payload.end_of_life = formData.end_of_life || null;
            payload.end_of_support = formData.end_of_support || null;

            const response =
                mode === 'edit' && asset?.id
                    ? await baseapi.put(`/assets/${asset.id}`, payload)
                    : await baseapi.post('/assets', payload);

            if (response.data.status) {
                showSuccess(mode === 'edit' ? 'Asset updated successfully' : 'Asset added successfully');
                onSuccess();
                handleClose();
            } else {
                showError(response.data.message || 'Failed');
            }
        } catch (error: any) {
            showError(error.message || 'Failed');
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]" onClick={handleClose} />

            <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4">
                <div className="w-full max-w-4xl bg-white dark:bg-gray-900/95 backdrop-blur-xl border border-gray-200 dark:border-orange-400/20 shadow-2xl rounded-xl text-gray-900 dark:text-white relative overflow-hidden max-h-[90vh] overflow-y-auto custom-scroll">
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />

                    <div className="sticky top-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-b border-gray-200 dark:border-gray-800 px-6 py-3 z-10">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <Network className="h-5 w-5 text-orange-400 mr-2" />
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                    {mode === 'edit' ? 'Edit Asset' : 'Add New Asset'}
                                </h2>
                            </div>
                            <button onClick={handleClose} className="text-gray-400 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white transition-colors p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6">
                        <div className="space-y-4">
                            {/* Row 1: Basic Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">
                                        <Server className="w-3 h-3 mr-1 text-blue-400" />
                                        Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className={`w-full bg-gray-50 dark:bg-gray-800/50 border ${errors.name ? 'border-red-400' : 'border-gray-300 dark:border-gray-700'} rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400`}
                                        placeholder="Core-Switch-01"
                                    />
                                    {errors.name && (
                                        <p className="mt-1 text-xs text-red-400 flex items-center">
                                            <AlertTriangle className="w-3 h-3 mr-1" /> {errors.name}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">
                                        <Network className="w-3 h-3 mr-1 text-purple-400" />
                                        Asset Type <span className="text-red-500">*</span>
                                    </label>
                                    <CustomDropdown
                                        data={assetTypes}
                                        width="w-full"
                                        selectedValue={formData.asset_type}
                                        placeholder="Select type"
                                        onSelect={(value: string) => setFormData(prev => ({
                                            ...prev,
                                            asset_type: value,
                                            ip_address: value === 'unmanaged_switch' ? '' : prev.ip_address
                                        }))}
                                        buttonHeight={10}
                                    />
                                </div>
                            </div>

                            {/* Row 2: Network Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {formData.asset_type !== 'unmanaged_switch' && (
                                    <div>
                                        <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">
                                            <Wifi className="w-3 h-3 mr-1 text-green-400" />
                                            IP Address <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="ip_address"
                                            value={formData.ip_address}
                                            onChange={handleInputChange}
                                            className={`w-full bg-gray-50 dark:bg-gray-800/50 border ${errors.ip_address ? 'border-red-400' : 'border-gray-300 dark:border-gray-700'} rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 font-mono focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400`}
                                            placeholder="192.168.1.10"
                                        />
                                        {errors.ip_address && (
                                            <p className="mt-1 text-xs text-red-400 flex items-center">
                                                <AlertTriangle className="w-3 h-3 mr-1" /> {errors.ip_address}
                                            </p>
                                        )}
                                    </div>
                                )}

                                <div className={formData.asset_type === 'unmanaged_switch' ? 'md:col-span-2' : ''}>
                                    <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">
                                        <Server className="w-3 h-3 mr-1 text-cyan-400" />
                                        Hostname
                                    </label>
                                    <input
                                        type="text"
                                        name="hostname"
                                        value={formData.hostname}
                                        onChange={handleInputChange}
                                        className="w-full bg-gray-50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
                                        placeholder="core-sw-01.local"
                                    />
                                </div>
                            </div>

                            {/* Row 3: Location */}
                            <div>
                                <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">
                                    <MapPin className="w-3 h-3 mr-1 text-amber-400" />
                                    Location
                                </label>
                                <input
                                    type="text"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    className="w-full bg-gray-50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
                                    placeholder="Server Room Rack 1"
                                />
                            </div>

                            {/* Configuration for Switch, Router, Firewall */}
                            {showPortFields && (
                                <div className="p-4 bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50 rounded-lg">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                        {/* Switch Role - Only show for switches */}
                                        {['switch', 'unmanaged_switch'].includes(formData.asset_type) && (
                                            <div>
                                                <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">
                                                    <Network className="w-3 h-3 mr-1 text-violet-400" />
                                                    Switch Role
                                                </label>
                                                <CustomDropdown
                                                    data={switchRoles}
                                                    width="w-full"
                                                    selectedValue={formData.switch_role}
                                                    placeholder="Select role"
                                                    onSelect={(value: string) => setFormData(prev => ({ ...prev, switch_role: value }))}
                                                    buttonHeight={10}
                                                />
                                            </div>
                                        )}

                                        {/* Total Ports - Show for switch, router, and firewall */}
                                        <div className={!['switch', 'unmanaged_switch'].includes(formData.asset_type) ? "md:col-span-2" : ""}>
                                            <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">
                                                <Network className="w-3 h-3 mr-1 text-sky-400" />
                                                Total Ports
                                            </label>
                                            <input
                                                type="number"
                                                name="total_ports"
                                                value={formData.total_ports}
                                                onChange={handleInputChange}
                                                min={1}
                                                max={999}
                                                className={`w-full bg-gray-50 dark:bg-gray-800/50 border ${errors.total_ports ? 'border-red-400' : 'border-gray-300 dark:border-gray-700'} rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 font-mono focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400`}
                                                placeholder="24"
                                            />
                                            {errors.total_ports && (
                                                <p className="mt-1 text-xs text-red-400 flex items-center">
                                                    <AlertTriangle className="w-3 h-3 mr-1" /> {errors.total_ports}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {showPrimaryOption && (
                                        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700/50">
                                            <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-2">
                                                <Circle className="w-3 h-3 mr-1 text-orange-400" />
                                                Is Primary Core Switch?
                                            </label>
                                            <div className="flex items-center gap-4">
                                                <label className="flex items-center gap-2 cursor-pointer group">
                                                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${formData.is_primary ? 'border-orange-500 bg-orange-500/20' : 'border-gray-400 dark:border-gray-600 group-hover:border-gray-500'}`}>
                                                        {formData.is_primary && <div className="w-2 h-2 rounded-full bg-orange-500" />}
                                                    </div>
                                                    <input
                                                        type="radio"
                                                        name="is_primary"
                                                        checked={formData.is_primary}
                                                        onChange={() => setFormData(prev => ({ ...prev, is_primary: true }))}
                                                        className="hidden"
                                                    />
                                                    <span className={`text-sm ${formData.is_primary ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>Yes</span>
                                                </label>

                                                <label className="flex items-center gap-2 cursor-pointer group">
                                                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${!formData.is_primary ? 'border-orange-500 bg-orange-500/20' : 'border-gray-400 dark:border-gray-600 group-hover:border-gray-500'}`}>
                                                        {!formData.is_primary && <div className="w-2 h-2 rounded-full bg-orange-500" />}
                                                    </div>
                                                    <input
                                                        type="radio"
                                                        name="is_primary"
                                                        checked={!formData.is_primary}
                                                        onChange={() => setFormData(prev => ({ ...prev, is_primary: false }))}
                                                        className="hidden"
                                                    />
                                                    <span className={`text-sm ${!formData.is_primary ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Tags */}
                            <div>
                                <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">
                                    <Tag className="w-3 h-3 mr-1 text-rose-400" />
                                    Tags
                                </label>
                                <div className="relative" ref={tagDropdownRef}>
                                    <div
                                        onClick={() => setIsTagDropdownOpen(!isTagDropdownOpen)}
                                        className={`min-h-[40px] w-full bg-gray-50 dark:bg-gray-800/50 border ${isTagDropdownOpen ? 'border-orange-400 ring-1 ring-orange-400' : 'border-gray-300 dark:border-gray-700'} rounded-lg p-1.5 flex flex-wrap gap-1.5 cursor-text transition-all items-center pr-8`}
                                    >
                                        {selectedTags.map(tag => (
                                            <span
                                                key={tag.id}
                                                className="flex items-center gap-1.5 px-2 py-0.5 bg-orange-600/10 border border-orange-500/20 text-orange-600 dark:text-orange-400 text-xs font-semibold rounded"
                                            >
                                                {tag.name}
                                                <X
                                                    className="w-3 h-3 cursor-pointer hover:text-orange-800 dark:hover:text-white transition-colors"
                                                    onClick={(e) => { e.stopPropagation(); handleToggleTag(tag); }}
                                                />
                                            </span>
                                        ))}

                                        <input
                                            type="text"
                                            className="bg-transparent border-none outline-none text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 flex-1 min-w-[100px] py-0.5 px-1"
                                            placeholder={selectedTags.length === 0 ? "critical, network, switch" : ""}
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                if (!isTagDropdownOpen) setIsTagDropdownOpen(true);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        />

                                        <ChevronDown className={`absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${isTagDropdownOpen ? 'rotate-180' : ''}`} />
                                    </div>

                                    {isTagDropdownOpen && (
                                        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl z-50 overflow-hidden">
                                            <div className="max-h-[200px] overflow-y-auto custom-scroll p-1">
                                                {isTagLoading && availableTags.length === 0 && (
                                                    <div className="flex items-center justify-center py-4 text-xs text-gray-400 gap-2">
                                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                        <span>Loading tags...</span>
                                                    </div>
                                                )}

                                                {!isTagLoading && availableTags.length === 0 && !searchTerm && (
                                                    <p className="py-4 text-xs text-gray-500 dark:text-gray-400 text-center italic font-medium">No tags available</p>
                                                )}

                                                {availableTags.map(tag => {
                                                    const isSelected = selectedTags.some(t => t.id === tag.id);
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={tag.id}
                                                            onClick={(e) => { e.stopPropagation(); handleToggleTag(tag); }}
                                                            className={`w-full flex items-center justify-between px-3 py-2 rounded text-xs transition-all ${isSelected ? 'bg-orange-600/10 text-orange-600 dark:text-orange-400 font-bold' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                                                                }`}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <Tag className={`w-3.5 h-3.5 ${isSelected ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400 dark:text-gray-500'}`} />
                                                                <span>{tag.name}</span>
                                                            </div>
                                                            {isSelected && <Check className="w-3.5 h-3.5 text-orange-500" strokeWidth={3} />}
                                                        </button>
                                                    );
                                                })}

                                                {searchTerm && !availableTags.some(t => t.name.toLowerCase() === searchTerm.toLowerCase()) && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); handleCreateTag(); }}
                                                        disabled={isCreatingTag}
                                                        className="w-full flex items-center gap-2 px-3 py-2 text-orange-600 dark:text-orange-400 bg-orange-500/5 hover:bg-orange-500/10 rounded mt-1 border border-dashed border-orange-500/30 text-xs font-semibold"
                                                    >
                                                        {isCreatingTag ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                                                        <span>Create "{searchTerm}"</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Select tags from list or type to create new ones</p>
                            </div>

                            {/* Section: Warranty & AMC */}
                            <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
                                <h3 className="text-xs font-bold text-orange-500 uppercase tracking-wider mb-4">Warranty & AMC Details</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">Purchase Date</label>
                                        <input
                                            type="date"
                                            name="purchase_date"
                                            value={formData.purchase_date}
                                            onChange={handleInputChange}
                                            className="w-full bg-gray-50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-orange-400"
                                        />
                                    </div>
                                    <div>
                                        <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">Installation Date</label>
                                        <input
                                            type="date"
                                            name="installation_date"
                                            value={formData.installation_date}
                                            onChange={handleInputChange}
                                            className="w-full bg-gray-50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-orange-400"
                                        />
                                    </div>
                                    <div>
                                        <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">Warranty Expiration</label>
                                        <input
                                            type="date"
                                            name="warranty_expiration"
                                            value={formData.warranty_expiration}
                                            onChange={handleInputChange}
                                            className="w-full bg-gray-50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-orange-400"
                                        />
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">Warranty Details</label>
                                    <textarea
                                        name="warranty_details"
                                        value={formData.warranty_details}
                                        onChange={handleInputChange}
                                        rows={2}
                                        className="w-full bg-gray-50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-400 resize-none"
                                        placeholder="Enter warranty coverage details..."
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                    <div>
                                        <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">AMC Provider</label>
                                        <input
                                            type="text"
                                            name="amc_provider"
                                            value={formData.amc_provider}
                                            onChange={handleInputChange}
                                            className="w-full bg-gray-50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-400"
                                            placeholder="Service Provider Name"
                                        />
                                    </div>
                                    <div>
                                        <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">AMC Expiration</label>
                                        <input
                                            type="date"
                                            name="amc_expiration"
                                            value={formData.amc_expiration}
                                            onChange={handleInputChange}
                                            className="w-full bg-gray-50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-orange-400"
                                        />
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">AMC Details</label>
                                    <textarea
                                        name="amc_details"
                                        value={formData.amc_details}
                                        onChange={handleInputChange}
                                        rows={2}
                                        className="w-full bg-gray-50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-400 resize-none"
                                        placeholder="Enter AMC terms and conditions..."
                                    />
                                </div>
                            </div>

                            {/* Section: Lifecycle */}
                            <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
                                <h3 className="text-xs font-bold text-orange-500 uppercase tracking-wider mb-4">Product Lifecycle</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">End of Life (EoL)</label>
                                        <input
                                            type="date"
                                            name="end_of_life"
                                            value={formData.end_of_life}
                                            onChange={handleInputChange}
                                            className="w-full bg-gray-50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-orange-400"
                                        />
                                    </div>
                                    <div>
                                        <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">End of Support (EoS)</label>
                                        <input
                                            type="date"
                                            name="end_of_support"
                                            value={formData.end_of_support}
                                            onChange={handleInputChange}
                                            className="w-full bg-gray-50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-orange-400"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={loading}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 disabled:opacity-50"
                            >
                                <X className="w-3 h-3" />
                                Cancel
                            </button>

                            <button
                                type="submit"
                                disabled={loading}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-50 min-w-[120px] justify-center"
                            >
                                {loading ? (
                                    <>
                                        <Network className="w-3 h-3" style={{ animation: 'spin 3s linear infinite' }} />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Check className="w-3 h-3" />
                                        {mode === 'edit' ? 'Update' : 'Add'}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default AddAssetModal;
